import LegoAdminPageContext from '../../legoAdminPageContext';
import MongoDocEditor from '../../../components/lego/MongoDocEditor';
import _ from 'lodash';
import { IconButton } from '../../../components/common/IconButton';
import TextSearchInput from '../../../components/common/TextSearchInput';
import { UserAvatar } from '../../../components/common/UserAvatar';
import { UserAvatarLive } from '../../../components/common/UserAvatarLive';
import BadgeId from '../../../components/common/BadgeId';
import { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';
import { fromNow } from '../../../components/common/TimeUtils';
import { TableWithSelection } from '../../../components/common/editors/TableWithSelection';

/** @type {import('react').ForwardRefExoticComponent<any & {ref: import('react').Ref<{getOperationsHistory: function(): Array, applyOperations: function(Array): void}>}>} */
const LiveTableComponent = forwardRef(function LiveTable ({ service, columns, forceRefresh, selectionActions, hideId, filter, limit = 100, onTextSearch }, ref) {
  const { page } = useContext(LegoAdminPageContext);
  const [objects, setObjects] = useState(null);
  const [textFilter, setTextFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(50);
  const [columnSort, setColumnSort] = useState(null);

  columns = _.mapValues(columns, (column) => {
    if (_.isFunction(column)) {
      return { sortBy: false, displayFunc: column };
    }
    return column;
  });

  function refresh(extraFilter = {}) {
    setLoading(true);
    setDisplayLimit(50);
  
    service.find({query: {$limit: limit, ... filter, ... extraFilter}}).then(setObjects).then(() => setSelected([])).finally(() => setLoading(false));
  }

  function refreshIds(ids, extraFilter = {}) {
    let idsFilter = { _id: { $in: ids } };
    setLoading(true);
    service.find({query: {$limit: limit, ... idsFilter, ... extraFilter}}).then((updatedObjects) => {      
      setObjects(objs => {
        const byId = _.keyBy(updatedObjects, '_id')
        // Remove missing objects
        _.each(ids, id => byId[id] = byId[id] || false);
        return _.compact(_.map(objs, o => byId[o._id] ?? o));
      });
    }).finally(() => setLoading(false));
  }

  useEffect(() => {
    refresh();
  }, [JSON.stringify(filter), limit]);

  // Exporse methods as ref
  useImperativeHandle(ref, () => {
    return {
      refresh() {
        refresh();
      },
      refreshIds(ids, extraFilter) {
        refreshIds(ids, extraFilter);
      },
      clearSelection() {
        setSelected([]);
      }
    };
  });

  async function openEdit(obj) {
    let freshObj = obj;
    if (freshObj && freshObj._id) {
      // freshObj = await this.refreshObject(obj);
      // this.setUrlParams({ editing: freshObj._id });
    }

    const defaultOnSaveCallback = async (changedObj, closeDialog) => {
      try {
        // let res = await this.updateOrCreate(freshObj, changedObj);
        //
        // if (closeDialog) {
        //   this.modalActionsBus.emit('close');
        // } else if (freshObj._id) {
        //   return await this.refreshObject(freshObj);
        // }
        // return res;
      } catch (err) {
        this.handleError(err);
      }
    };

    page.openModal(<div>
      <MongoDocEditor obj={obj} onSave={defaultOnSaveCallback} onCancel={() => page.closeModal()}/>
    </div>, {
      fullScreen: false,
      onClose: () => page.deleteUrlParam('editing'),
      title: `${obj?._id ? 'Edit' : 'Create'} document`
    });
  }

  if (objects) {
    let filteredObjects = objects;
    if (textFilter) {
      let regex = new RegExp(_.escapeRegExp(textFilter), 'i');
      try {
        regex = new RegExp(textFilter, 'i');
      } catch(err) {    
      }
      filteredObjects = _.filter(objects, o => regex.test(JSON.stringify(o).toLowerCase()));
    }

    // Apply sorting
    let sortedAndFilteredObjects = filteredObjects;
    if (columnSort) {
      const { key, asc, sortBy } = columnSort;
      sortedAndFilteredObjects = _.orderBy(filteredObjects, sortBy || (o => _.get(o, key)), [asc ? 'asc' : 'desc']);
    }

    let nColumns = _.toPairs(columns).length + 1 + (hideId ? 0 : 1);

    let onFireSearch = () => {
      if(textFilter) {
        onTextSearch(textFilter);
        setTextFilter('');
      }
    };

    const onHeaderClick = (key, sortBy) => {
      if (sortBy) {
        setColumnSort(prevSort => {
          if (prevSort?.key === key) {
            // Toggle direction if same column is clicked
            return { ...prevSort, asc: !prevSort.asc };
          } else {
            // Sort ascending by default on new column
            return { key, sortBy, asc: true };
          }
        });
      }
    };

    return <div className={'border'}>  
      <TableWithSelection
        selected={selected}
        onSelectionChange={(newSelected) => setSelected(newSelected)}
        className={loading ? 'translucent' : ''}
      >
        <thead>
        <tr className={'sticky-top bg-light'}>
          <th colSpan={nColumns} className={'text-info'}>
            <div className={'d-flex align-items-center'}>

              <TextSearchInput className={'form-control-sm mr-2'} style={{ maxWidth: '300px' }} value={textFilter}
                               onChange={setTextFilter} onEnter={onFireSearch} placeholder={'Filter rows...'}/>
            
              {textFilter && onTextSearch ? <IconButton icon={'filter_alt'} onClick={onFireSearch}/> : null}

              {textFilter ? sortedAndFilteredObjects.length + '/' : ''}{objects.length} rows


              {selected.length ? <div className={'ml-5 text-dark d-flex align-items-center'}>
                {selected.length} selected

                {selectionActions && selectionActions(_.filter(objects, o => selected.includes(o._id)))}
              </div> : null}
            </div>
          </th>
        </tr>

        <tr>
          <th>{}</th>
          {hideId ? null : <th>Id</th>}
          {_.map(columns, (columnDef, key) => {
            // Support both simple functions and object definitions for columns
            const { header, sortBy } = _.isFunction(columnDef) ? { header: key, sortBy: false } : columnDef;
            const isSortable = !!sortBy;
            const isCurrentSort = columnSort?.key === key;
            let sortIcon = null;
            let sortButtonLevel = 'secondary'; // Default level for sort button

            if (isSortable) {
              if (isCurrentSort) {
                sortIcon = columnSort.asc ? 'arrow_upward' : 'arrow_downward';
                sortButtonLevel = 'primary'; // Highlight active sort button
              } else {
                sortIcon = 'swap_vert'; // Icon indicating sortability
              }
            }

            return (
              <th
                key={key}
                style={isSortable ? { cursor: 'default' } : {}} // Remove pointer cursor from th if button handles click
              >
                {header || key}
                {isSortable && (
                  <IconButton
                    icon={sortIcon}
                    level={sortButtonLevel}
                    onClick={() => onHeaderClick(key, sortBy)}
                    className={'ml-1 p-0'} // Minimal padding and margin
                    size={'small'} // Make the button small
                    description={`Sort by ${header || key}`} // Tooltip
                  />
                )}
              </th>
            );
          })}
        </tr>
        </thead>
        <tbody>
          {_.compact(_.map(sortedAndFilteredObjects.slice(0, displayLimit), o => {
            let { _id, createdBy, updatedBy, createdAt, updatedAt } = o;
            updatedBy = updatedBy || createdBy;
            updatedAt = updatedAt || createdAt;

            return <tr key={o._id}>
              {hideId ? null : <td className={'no-wrap'}>
                {updatedBy ? ((updatedBy?.userId) ? <UserAvatar user={updatedBy}/> :
                  <UserAvatarLive id={updatedBy?.id || updatedBy}/>) : null}
                <span className={'d-inline-block ml-2 align-middle'}>
                  <span className={'text-info small'} title={updatedAt ? new Date(updatedAt).toString() : ''}>{updatedAt ? fromNow(updatedAt) : ''}</span>
                  <br/>
                  <BadgeId id={_id}/>
                </span>
              </td>}

              {_.map(columns, ({sortBy, displayFunc}, k) => {
                let cellValue = displayFunc(o);
                if( cellValue?.type === 'td') {
                  return cellValue;
                } else {
                  return <td key={k}>{cellValue}</td>;
                }
              })}
            </tr>;
          }))}

          { onTextSearch && textFilter ? <tr key={'text-filter'}>
            <td colSpan={nColumns} className={'text-center p-1 bg-light-primary'}>
              <IconButton icon={'filter_alt'} onClick={() => setTextFilter('') + onTextSearch(textFilter)}>
                Use text filter "{textFilter}" in database query for more results
              </IconButton>
            </td>
          </tr> : null }

          {displayLimit < sortedAndFilteredObjects.length ? <tr key={'show-more'}>
            <td colSpan={nColumns} className={'text-center p-1 bg-light-primary'} >
              <IconButton icon={'visibility'} onClick={() => setDisplayLimit(sortedAndFilteredObjects.length)}>
                Show the remaining {sortedAndFilteredObjects.length - displayLimit} rows
              </IconButton>
            </td>
          </tr> : null}
        </tbody>
      </TableWithSelection>
    </div>;
  } else {
    return <div className={'p-3'}>Loading...</div>;
  }
});

export default LiveTableComponent;
